import { useEffect, useState, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { User } from "@supabase/supabase-js";

// Image mapping
import iphone15Pro from "@/assets/iphone-15-pro.jpg";
import macbookAirM3 from "@/assets/macbook-air-m3.jpg";
import airpodsPro from "@/assets/airpods-pro.jpg";
import appleWatch9 from "@/assets/apple-watch-9.jpg";
import ipadProM2 from "@/assets/ipad-pro-m2.jpg";
import iphone17Pro from "@/assets/iphone-17-pro.jpg";
import macM5 from "@/assets/mac-m5.jpg";

const imageMap: Record<string, string> = {
  "iphone-15-pro": iphone15Pro,
  "macbook-air-m3": macbookAirM3,
  "airpods-pro": airpodsPro,
  "apple-watch-9": appleWatch9,
  "ipad-pro-m2": ipadProM2,
  "iphone-17-pro": iphone17Pro,
  "mac-m5": macM5,
};

export interface Article {
  id: string;
  slug: string;
  title: string;
  description: string;
  image: string;
  views_count: number;
  likes_count: number;
  created_at: string;
  content?: { type: string; content: string }[];
}

export const useArticles = (user: User | null) => {
  const [articles, setArticles] = useState<Article[]>([]);
  const [userLikes, setUserLikes] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(true);

  // Fetch articles - show published articles OR those with scheduled publish time passed
  const fetchArticles = useCallback(async () => {
    const now = new Date().toISOString();
    
    // Articles should be visible if:
    // 1. is_published is true, OR
    // 2. published_at is set and has passed (scheduled publish)
    const { data, error } = await supabase
      .from("articles")
      .select("*")
      .or(`is_published.eq.true,and(published_at.not.is.null,published_at.lte.${now})`)
      .order("created_at", { ascending: false });

    if (error) {
      console.error("Error fetching articles:", error);
      return;
    }

    const mappedArticles = data.map((article) => ({
      id: article.id,
      slug: article.slug,
      title: article.title,
      description: article.description,
      image: imageMap[article.image_key] || article.image_key,
      views_count: article.views_count,
      likes_count: article.likes_count,
      created_at: article.created_at,
      content: article.content as { type: string; content: string }[] | undefined,
    }));

    setArticles(mappedArticles);
    setLoading(false);
  }, []);

  // Fetch user likes
  const fetchUserLikes = useCallback(async () => {
    if (!user) {
      setUserLikes(new Set());
      return;
    }

    const { data, error } = await supabase
      .from("article_likes")
      .select("article_id")
      .eq("user_id", user.id);

    if (error) {
      console.error("Error fetching user likes:", error);
      return;
    }

    setUserLikes(new Set(data.map((like) => like.article_id)));
  }, [user]);

  // Record a view
  const recordView = useCallback(async (articleId: string) => {
    if (!user) return;

    // Try to insert a view (will fail silently if already exists due to unique constraint)
    const { error } = await supabase
      .from("article_views")
      .insert({ article_id: articleId, user_id: user.id });

    if (error && !error.message.includes("duplicate")) {
      console.error("Error recording view:", error);
    }
  }, [user]);

  // Toggle like
  const toggleLike = useCallback(async (articleId: string) => {
    if (!user) return;

    const isLiked = userLikes.has(articleId);

    if (isLiked) {
      // Remove like
      const { error } = await supabase
        .from("article_likes")
        .delete()
        .eq("article_id", articleId)
        .eq("user_id", user.id);

      if (error) {
        console.error("Error removing like:", error);
        return;
      }

      setUserLikes((prev) => {
        const newSet = new Set(prev);
        newSet.delete(articleId);
        return newSet;
      });
    } else {
      // Add like
      const { error } = await supabase
        .from("article_likes")
        .insert({ article_id: articleId, user_id: user.id });

      if (error) {
        console.error("Error adding like:", error);
        return;
      }

      setUserLikes((prev) => new Set(prev).add(articleId));
    }
  }, [user, userLikes]);

  // Initial fetch
  useEffect(() => {
    fetchArticles();
    fetchUserLikes();
  }, [fetchArticles, fetchUserLikes]);

  // Set up realtime subscription for articles updates
  useEffect(() => {
    const channel = supabase
      .channel("articles-realtime")
      .on(
        "postgres_changes",
        {
          event: "UPDATE",
          schema: "public",
          table: "articles",
        },
        (payload) => {
          const updated = payload.new as any;
          setArticles((prev) =>
            prev.map((article) =>
              article.id === updated.id
                ? {
                    ...article,
                    views_count: updated.views_count,
                    likes_count: updated.likes_count,
                  }
                : article
            )
          );
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, []);

  return {
    articles,
    userLikes,
    loading,
    recordView,
    toggleLike,
    refetch: fetchArticles,
  };
};
